import React from "react";
import { Dialog, DialogContent, Typography, Box, Button } from "@mui/material";
import { Check, Clear } from "@mui/icons-material";

interface PaymentResultPopupProps {
  show: boolean;
  onClose: () => void;
  result: {
    type: 'success' | 'failed' | 'abort';
    orderid: string;
    status: string;
    credits?: number;
    amount?: number;
    currency?: string;
  };
}

const PaymentResultPopup: React.FC<PaymentResultPopupProps> = ({ show, onClose, result }) => {
  if (!show) return null;

  const isSuccess = result.type === 'success';
  const isVerifying = result.status.includes('Verifying') || result.status.includes('Checking');

  return (
    <Dialog
      open={show}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          minWidth: 400,
          maxWidth: 440,
          minHeight: 300,
          p: 1,
        },
      }}
    >
      {/* Loading state */}
      {isVerifying ? (
        <DialogContent
          sx={{
            pt: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box mb={2}>
            <div style={{
              width: 72,
              height: 72,
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #198754',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              marginBottom: 24
            }} />
          </Box>
          <Typography align="center" variant="h5" gutterBottom>
            Verifying Payment...
          </Typography>
          <Typography align="center" variant="body1" gutterBottom>
            {result.status}
          </Typography>
        </DialogContent>
      ) : (
        /* Success/Failure Result */
        <DialogContent
          sx={{
            pt: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            mb={1}
            sx={{ display: "flex", justifyContent: "center", padding: 2 }}
          >
            <Box
              sx={{
                border: `8px solid ${isSuccess ? '#198754' : '#dc3545'}`,
                borderRadius: "50%",
                padding: 1,
              }}
            >
              {isSuccess ? (
                <Check
                  sx={{
                    width: "60px",
                    height: "60px",
                    color: "#198754",
                  }}
                />
              ) : (
                <Clear
                  sx={{
                    width: "60px",
                    height: "60px",
                    color: "#dc3545",
                  }}
                />
              )}
            </Box>
          </Box>
          <Box mb={2}>
            <Typography align="center" variant="h5" gutterBottom>
              {isSuccess ? 'Payment Successful!' : 'Payment Failed!'}
            </Typography>
            <Typography align="center" variant="body1" gutterBottom>
              {isSuccess && result.credits 
                ? `${result.credits} Credit${result.credits > 1 ? "s" : ""} added to your account.`
                : result.status}
            </Typography>
          </Box>

          {/* Continue Button */}
          <Button
            onClick={onClose}
            variant="contained"
            sx={{
              backgroundColor: isSuccess ? '#198754' : '#dc3545',
              "&:hover": { 
                backgroundColor: isSuccess ? '#157347' : '#bb2d3b' 
              },
              paddingX: 4,
              paddingY: 1.5,
              fontSize: 15,
              fontWeight: 500,
            }}
          >
            Continue Chatting
          </Button>
        </DialogContent>
      )}

      {/* CSS for spinner animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </Dialog>
  );
};

export default PaymentResultPopup;